import { fork } from 'effector';
import type { Scope, Store } from 'effector';
import {
  $catalogCategories,
  $catalogId,
  $catalogProducts,
  $cityPhoneData,
  $stopLists,
  type CatalogCategory,
  type CatalogProducts,
} from 'domains/catalog';
import {
  $allCitiesList,
  $citiesList,
  $city,
  $countriesList,
  $country,
  $currency,
  $deliveryZone,
  $deliveryZonesAndMapLayerZones,
  $restaurant,
  $restaurantsList,
  $timeIntervals,
  $type,
  type City,
  type Country,
  type Restaurant,
} from 'domains/cartography';
import { $language } from 'domains/language';
import type CityPhoneData from 'models/cityPhoneData';
import type { CurrencySymbol, Language, OrderType } from 'models';
import { $seoPublicOrigin, $notFoundPath } from '../../shared/seo/model';
import { normalizePublicOrigin, PUBLIC_ORIGIN } from '../../shared/seo/urls';

export const SSR_SEO_APP_STATE_SCRIPT_ID = '__SSR_SEO_APP_STATE__';

export type SsrSeoPublicAppState = {
  version: 1;
  branch: 'searchBot';
  publicOrigin?: string;
  notFoundPath?: string;
  catalog: {
    catalogId: string | null;
    products: CatalogProducts | null;
    categories: CatalogCategory[];
    stopLists: null;
    cityPhoneData: CityPhoneData;
  };
  cartography: {
    city: City | null;
    cities: City[];
    allCities: City[];
    country: Country | null;
    countries: Country[];
    restaurant: Restaurant | null;
    restaurants: Restaurant[];
    type: Exclude<OrderType, 'IN_PLACE'>;
    currency: CurrencySymbol;
    deliveryZone: null;
    deliveryZonesAndMapLayerZones: [];
    timeIntervals: null;
  };
  language: Language;
};

const createStoreValues = (
  state: SsrSeoPublicAppState,
): Array<[Store<any>, any]> => [
  [$seoPublicOrigin, normalizePublicOrigin(state.publicOrigin || PUBLIC_ORIGIN)],
  [$notFoundPath, state.notFoundPath || null],
  [$catalogProducts, state.catalog.products],
  [$catalogCategories, state.catalog.categories],
  [$catalogId, state.catalog.catalogId],
  [$stopLists, state.catalog.stopLists],
  [$cityPhoneData, state.catalog.cityPhoneData],
  [$city, state.cartography.city],
  [$citiesList, state.cartography.cities],
  [$allCitiesList, state.cartography.allCities],
  [$country, state.cartography.country],
  [$countriesList, state.cartography.countries],
  [$restaurant, state.cartography.restaurant],
  [$restaurantsList, state.cartography.restaurants],
  [$type, state.cartography.type],
  [$currency, state.cartography.currency],
  [$deliveryZone, state.cartography.deliveryZone],
  [$deliveryZonesAndMapLayerZones, state.cartography.deliveryZonesAndMapLayerZones],
  [$timeIntervals, state.cartography.timeIntervals],
  [$language, state.language],
];

export const createSsrSeoAppScope = (
  state: SsrSeoPublicAppState,
): Scope =>
  fork({
    values: createStoreValues(state),
  });

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isSsrSeoPublicAppState = (
  value: unknown,
): value is SsrSeoPublicAppState => {
  if (!isRecord(value)) return false;

  return (
    value.version === 1 &&
    value.branch === 'searchBot' &&
    isRecord(value.catalog) &&
    isRecord(value.cartography)
  );
};

export const parseSsrSeoPublicAppState = (
  rawState: string | null | undefined,
): SsrSeoPublicAppState | null => {
  if (!rawState) return null;

  try {
    const parsed = JSON.parse(rawState);

    return isSsrSeoPublicAppState(parsed) ? parsed : null;
  } catch {
    return null;
  }
};
